import { RaceRaw } from './interfaces/race_raw.interface';
import { CloudFile } from './cloudFile';
import { CityRaw } from './interfaces/city_raw.interface';
import { Marshal } from './marshal';
import { Org } from './org';
import { RaceDocument } from './race_document';

export class SportType {
  code: string = '';
  name: string = '';
  icon: string = '';
  description: string = '';
  equipment: string = '';

  constructor(obj: SportType) {
    this.code = obj.code;
    this.name = obj.name;
    this.icon = obj.icon;
    this.description = obj.description;
    this.equipment = obj.equipment;
  }
}

export class GpsPoint {
  latitude: number = 0.0;
  longitude: number = 0.0;
  description: string = '';
  constructor(obj: GpsPoint) {
    this.latitude = obj.latitude;
    this.longitude = obj.longitude;
    this.description = obj.description;
  }
}

export class City {
  id: string = '';
  name: string = '';
  address: string = '';
  countryId: string = '';
  utcOffset: number = 0;
  gps!: GpsPoint;
  constructor(obj: CityRaw) {
    this.id = obj.id;
    this.name = obj.name;
    this.address = obj.address;
    this.countryId = obj.countryId;
    this.utcOffset = obj.utcOffset;
    this.gps = new GpsPoint(obj.gps);
  }
}

export type Question = {
  value: string;
  obligatory: boolean;
}

export class Race {
  rUid!: string;
  name: string = '';
  date: Date = new Date();
  private: boolean = false;
  deleted: boolean = false;
  deletedTimestamp: string = '';
  deletedAuthor: string = '';

  city!: City;
  location!: GpsPoint;
  offset = "+0000";

  sportType!: SportType;
  infoFieldName: string = '';
  description: string = '';
  applicationText: string = '';

  logo: string = '';
  titlePicture: string = '';
  protocolsLogoLeft: string = '';
  protocolsLogoCenter: string = '';
  protocolsLogoRight: string = '';

  isMultistage: boolean = false;
  stageOf: string = '';

  raceState: number = 0;
  startRegistration: Date | null = null;
  endRegistration: Date | null = null;

  partialLapCountMethod: boolean = true;
  totalsCountBy: boolean = false;
  showOnlyConfirmed: boolean = false;
  hasTeamStandings: boolean = false;
  hasOverallStandings: boolean = false;
  resultByBestLap: boolean = false;
  doNotCountFirstLapAsBest: boolean = false;
  showMillsInResults: boolean = false;
  allowEqualNumbers: boolean = false;
  maxCompetitors: number = 0;

  useNfc: boolean = false;
  useRfid: boolean = false;
  useGps: boolean = true;
  useCurrentLoop: boolean = false;

  orgs: Org[] = [];
  orgTeam: string = '';

  documents: RaceDocument[] = [];

  questions: Question[] = [];

  marshals: Marshal[] = [];

  isCompetitor: boolean = false;
  registrationClass: string = '';
  isOrg: boolean = false;
  isMedia: boolean = false;

  isRegistrationConfirmed: boolean = false;
  isPaid: boolean = false;
  isMediaConfirmed: boolean = false;

  competitorsCount: number = 0;
  competitorsStarted: number = 0;
  competitorsFinished: number = 0;
  raceRating: number = 0;
  raceRatingCount: number = 0;
  isAlreadyRated: boolean = false;

  files: CloudFile[] = [];
  links: CloudFile[] = [];
  images: CloudFile[] = [];

  constructor(raw: RaceRaw) {
    this.rUid = raw.rUid;
    this.name = raw.name;
    this.date = new Date(raw.date);
    this.private = raw.private;
    this.deleted = raw.deleted;
    if (this.deleted) {
      this.deletedTimestamp = new Date(raw.deletedTimestamp).toLocaleString();
      this.deletedAuthor = raw.deletedAuthor;
    }

    this.city = raw.city;
    this.location = raw.location;
    if (raw.city && raw.city.utcOffset) {
      const utcOffsetInMinutes = raw.city.utcOffset;

      // Преобразуем в часы и минуты в формате '+hhmm' или '-hhmm'
      const sign = utcOffsetInMinutes >= 0 ? "+" : "-";
      const absOffset = Math.abs(utcOffsetInMinutes);
      const hours = Math.floor(absOffset / 60);
      const minutes = absOffset % 60;

      // Формируем строку в нужном формате
      this.offset = `${sign}${String(hours).padStart(2, "0")}${String(
        minutes
      ).padStart(2, "0")}`;
    } else this.offset = "+0000";

    this.sportType = raw.sportType;
    this.infoFieldName = raw.infoFieldName;
    this.description = raw.description;
    this.applicationText = raw.applicationText;

    this.logo = raw.logo;
    this.titlePicture = raw.titlePicture;
    this.protocolsLogoLeft = raw.protocolsLogoLeft;
    this.protocolsLogoCenter = raw.protocolsLogoCenter;
    this.protocolsLogoRight = raw.protocolsLogoRight;

    this.isMultistage = raw.isMultistage;
    this.stageOf = raw.stageOf;

    this.raceState = raw.raceState;
    this.startRegistration = raw.startRegistration
      ? new Date(raw.startRegistration)
      : null;
    this.endRegistration = raw.endRegistration
      ? new Date(raw.endRegistration)
      : null;

    this.partialLapCountMethod = raw.partialLapCountMethod;
    this.totalsCountBy = raw.totalsCountBy;
    this.showOnlyConfirmed = raw.showOnlyConfirmed;
    this.hasTeamStandings = raw.hasTeamStandings;
    this.hasOverallStandings = raw.hasOverallStandings;
    this.resultByBestLap = raw.resultByBestLap;
    this.doNotCountFirstLapAsBest = raw.doNotCountFirstLapAsBest;
    this.showMillsInResults = raw.showMillsInResults;
    this.allowEqualNumbers = raw.allowEqualNumbers;
    this.maxCompetitors = raw.maxCompetitors;

    this.useNfc = raw.useNfc;
    this.useRfid = raw.useRfid;
    this.useGps = raw.useGps;
    this.useCurrentLoop = raw.useCurrentLoop;

    this.orgs.push(new Org(raw.orgs));
    this.orgTeam = raw.orgTeam;

    this.documents = raw.documents.map((document) => {
      document.checkTime = new Date(document.checkTime);
      if (!document.files) {
        document.files = [];
      }
      return document;
    });

    this.questions = raw.questions;

    this.marshals = raw.marshals;

    this.isCompetitor = raw.isCompetitor;
    this.registrationClass = raw.registrationClass;
    this.isOrg = raw.isOrg;
    this.isMedia = raw.isMedia;

    this.isRegistrationConfirmed = raw.isRegistrationConfirmed;
    this.isPaid = raw.isPaid;
    this.isMediaConfirmed = raw.isMediaConfirmed;

    this.competitorsCount = raw.competitorsCount;
    this.competitorsStarted = raw.competitorsStarted;
    this.competitorsFinished = raw.competitorsFinished;
    this.raceRating = raw.raceRating;
    this.raceRatingCount = raw.raceRatingCount;
    this.isAlreadyRated = raw.isAlreadyRated;

    if (raw.attachments) {
      this.images = raw.attachments.images.map((item) => new CloudFile(item.uid, item.file, item.description, item.isPrivate, item.docType, item.validUntil));
      this.files = raw.attachments.files.map((item) => new CloudFile(item.uid, item.file, item.description, item.isPrivate, item.docType, item.validUntil));
      this.links = raw.attachments.links.map((item) => new CloudFile(item.uid, item.file, item.description, item.isPrivate, item.docType, item.validUntil));
    }
  }
}
